import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { placeObjects } from '../../core/placeObjects';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/helpers';

/**
 * Pismena (Letters) - Letter placement with PlaceObjects
 *
 * Layout: 10 portrait pages.
 * Each page: image (top-left), spaced label text (top-right),
 * and a bordered area with randomly placed letters.
 *
 * Migrated from C# EduFrame.Img10.Generators.Pismena
 */
export class Pismena implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const border = PdfUnit.FromMillimeters(15);
    const fontBig = new PdfFont(PdfFont.FONT_NAME, 100);
    const fontSmall = new PdfFont(PdfFont.FONT_NAME, 50);
    const {random} = opts.data;

    const count = Math.min(this.countMax(), opts.list.length);

    for (let i = 0; i < count; i++) {
      const page = pdf.addPage();

      const ss = PdfUnit.FromMillimeters(60);
      const imgSize = new PdfSize(ss, ss);

      const label = opts.list[i].text.toUpperCase();
      // Space out letters: "A B C"
      const spaced = label.split('').join(' ');

      page.drawLabel(opts.list[i].label, {
        rect: new PdfRect(border, border, imgSize.width, imgSize.height),
      });
      page.drawFillStringCenter(
        spaced,
        fontBig,
        PdfBrush.Black,
        new PdfRect(
          border + imgSize.width + 20,
          border,
          PdfSize.A4.width - 2 * border - imgSize.width - 20,
          imgSize.height * 0.75
        )
      );

      const rect = new PdfRect(
        border,
        border + imgSize.height + 20,
        PdfSize.A4.width - 2 * border,
        PdfSize.A4.height - 2 * border - imgSize.height - 20
      );
      page.drawRectangle(GUtil.basePen, null, rect);

      // Build letter list: label letters + same count of random other letters
      const letterSize = new PdfSize(60, 60);
      const items: string[] = [];

      // Track which letters are used
      const usedLetters = new Set<string>();
      for (const ch of label) {
        items.push(ch);
        usedLetters.add(ch);
      }

      // Add random other letters
      const availableLetters: string[] = [];
      for (let ci = 0; ci < 26; ci++) {
        const ch = String.fromCharCode(65 + ci);
        if (!usedLetters.has(ch)) {
          availableLetters.push(ch);
        }
      }

      // Shuffle and take label.length
      for (let j = availableLetters.length - 1; j > 0; j--) {
        const k = random.next(0, j + 1);
        [availableLetters[j], availableLetters[k]] = [availableLetters[k], availableLetters[j]];
      }
      const extra = availableLetters.slice(0, label.length);
      items.push(...extra);

      const placed = placeObjects(random, rect, letterSize, items);

      for (const obj of placed) {
        page.drawString(obj.data, fontSmall, PdfBrush.Black, null, obj.rect, ETextAlign.Center);
      }
    }
  }
}
